import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { WishlistOutlined } from 'jl-design-system/components/icons/WishlistOutlined';
import { AddToWishListNotification } from './AddToWishListNotification';
import { isMin1024pxWide } from '../../utils/device/identifyDevice';

import styles from './account-wishlist.scss';

const popupDisplayDurationMs = 10000;

interface AccountWishListProps {
  wishListUrl: string;
  signInUrl: string;
  registerUrl: string;
  signedIn: boolean;
}

export const AccountWishList: React.FC<AccountWishListProps> = ({
  wishListUrl,
  signInUrl,
  registerUrl,
  signedIn,
}) => {
  const [isNotificationDisplayed, setIsNotificationDisplayed] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const startCloseTimer = useCallback(() => {
    // Clear any existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start new timer
    timeoutRef.current = setTimeout(() => {
      setIsNotificationDisplayed(false);
    }, popupDisplayDurationMs);
  }, []);

  const handleWishListEvent = useCallback(() => {
    if (isMin1024pxWide()) {
      setIsNotificationDisplayed(true);
      startCloseTimer();
    }
  }, [startCloseTimer]);

  const handleClosePopup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsNotificationDisplayed(false);
  };

  useEffect(
    function listenForGuestWishlistSuccess() {
      window.addEventListener(
        'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
        handleWishListEvent
      );

      return function stopListeningForGuestWishlistSuccess() {
        window.removeEventListener(
          'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
          handleWishListEvent
        );
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    },
    [handleWishListEvent]
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <a
        href={wishListUrl}
        className={classNames(styles.link, styles.desktop)}
        data-test="wishlist-button"
        data-testid="wishlist-button"
        aria-label="View your wishlist"
      >
        <WishlistOutlined className={styles.icon} size="medium" />
      </a>
      {!signedIn && isNotificationDisplayed && (
        <AddToWishListNotification
          signInUrl={signInUrl}
          registerUrl={registerUrl}
          onClose={handleClosePopup}
        />
      )}
    </div>
  );
};
